import { Checkbox, Dropdown, SingleDatePicker, SubmitButton, TextInput } from '@components/common';
import { useFormik } from 'formik';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useValidation } from '@hooks/validations';
import { Wrapper } from '@components/common/Wrapper/wrapper';
import '@assets/styles/pages/addNewMeetings.scss';
import {
	defaultAddMeetingInitialValues,
	defaultRBDMAddMeetingInitialValues,
	fccProductAwarnessStartDate,
	IDefaultAddMeetingFormInitialValues,
	IRBDMAddMeetingFormInitialValues,
	canSelectMultipleOfficeBearerClubs,
	isOfficeBearerMeetType,
	IsLcdWiseUserMeet,
	LCD_MEETS_SHOP_HALL,
	MaxLimitMap,
	MEETING_HIDE_CLUB,
	MEETING_HIDE_FOCUS_PRODUCT,
	MEETING_PARTICIPANTS_REMOVE,
	MEETING_THEME_HIDE_MEETING_TYPE,
	SocialMeetSubTypes,
	SocialMeetType,
	START_DATE_RESTRICTION_MEET,
	TSIMeetingTypes,
} from '.';
import { TimePicker } from '@components/common/time-picker/time-picker';
import {
	calculateEndTimeAndDate,
	compareDatePartsOnly,
	convertTime,
	extractTime,
	extractDateFromISO,
	formatDate,
	formatTime,
	generateTimeOptions,
	getCoordinatesFromTownName,
	isTimePast,
	keyDownEvent,
	RoleType,
	roundToNearestQuarterHour,
	getTownInfo,
} from '@helper/utils';
import { useMeetingMutation } from '@services/hooks/meetings/useMeetingMutation';
import { ClubDetails, IMRCode, TownList } from '@services/hooks/enduser-details';
import { useGetRxDb } from '@hooks/getRxdbData';
import { useTownDetailsQuery } from '@services/hooks/enduser-details/useTownDetailsQuery';
import { ProfileData } from '@services/hooks/profile';
import { useProfileQuery } from '@services/hooks/profile/useProfileQuery';
import { BIG_MEETS, CITY_LEAVEL_OB_MEET, meetFormate, meetingTheme, monthNames } from '@config/constant';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import MultiselectDropdown from '@components/common/formControl/multiselect-dropdown/multiselectDropdown';
import { useMeetingsTypesQuery } from '@services/hooks/meetings/useMeetingTypesQuery';
import { MeetingDetails, MeetingType, RBDMClubDropdownItem, RBDMMeetingDetails } from '@services/hooks/meetings';
import { useNonAttendedMembersStore } from '@store/nonAttendedMembersStore';
import { AddProductsFields } from '../productFields/addProductFileds';
import { useMeetingsListQuery } from '@services/hooks/meetings/useMeetingsListQuery';
import { ToastType, useTosterStore } from '@store/toster';
import { useGetMeetingQuery } from '@services/hooks/meetings/useGetMeetingQuery';
import { useSetMainHeader } from '@hooks/useSetMainHeader';
import { Modal } from '@components/common/modal/modal';
import { useGetClubRecommendationsMutation } from '@services/hooks/meetings/useGetClubRecomdationsQuery';
import { ClubRecommendation } from '../clubRecommendations/clubRecommendationModal';
import { ClubRecommendationResponse } from '../clubRecommendations';
import { IPrimaryDealerList } from '@components/onetoonemeetings';
import { useComponentLevelAccessCheck } from '../../../../authGuard/hooks/useComponentLevelAccessCheck';
import { useIMRCodeListQuery } from '@services/hooks/enduser-details/useIMRCodeListQuery';
import { DropdownOptions } from '@components/common/formControl/dropdown';
import GoogleSearchInput from '@components/googleSearch/googleSearch';
import { IMapSearchInput } from '@components/googleSearch';
import { useRBDMMeetingMutation } from '@services/hooks/meetings/useRBDMMeetingMutation';
import { CMDIDropdownRBDMItem } from '@services/hooks/endusers/cmdiDropdownRBDM';
import { useCMDIDropdownRBDMQuery } from '@services/hooks/endusers/useCMDIDropdownRBDMQuery';
import { useRBDMClubDropdownQuery } from '@services/hooks/meetings/useRBDMClubDropdownQuery';
import { PRODUCT_SPECIFIC_MEETS } from '../addNewMdpMeeting';

interface RBDMClubOption {
	id: string;
	value: string;
	sfid: string;
	name: string;
	assigned_cmdi: string;
}
import { useClubDropDownQuery } from '@services/hooks/endusers/clubListQuery';
import { useRbdmTownDetailsQuery } from '@services/hooks/meetings/useRbdmTownList';
import { useDealerListRxDb } from '@services/hooks/enduser-details/rxdbHooks/useDealerListRxDb';
import { useDebounce } from '@hooks/useDebounce';
import { AvailableProducts } from '@components/siteLeads/components/siteProducts';
import { AvailableProductsList } from '@services/hooks/site-leads';

export const AddNewMeeting = (): ReactElement => {
	const { state } = useLocation();
	const isMDPMeeting = state?.type === 'MDP';
	const { AddMeetingValidationSchema, RBDMAddMeetingValidationSchema } = useValidation();
	const { setNonAttendedMembers, nonAttendedMembers, resetMembersStore } = useNonAttendedMembersStore();
	const { mutate, isPending: isMeetingFormPending } = useMeetingMutation(state?.id, state?.type);
	const { mutate: rbdmMutate, isPending: isRBDMMeetingFormPending } = useRBDMMeetingMutation();
	const { result: availableProductsDataList } = useGetRxDb<AvailableProductsList>('products');
	const { refetch } = useMeetingsListQuery();
	const { setToaster } = useTosterStore();
	const [isCoordinatesFetching, setIsCoordinatesFetching] = useState(false);
	const { data, isFetching, error } = useGetMeetingQuery(state?.id, state?.type);
	const { data: clubDropDownData, refetch: refetchClubDropDown } = useClubDropDownQuery(true);
	useSetMainHeader(state?.id ? data?.name : 'Add New Meeting');
	const [isLocationPermissionGranted, setIsLocationPermissionGranted] = useState<boolean>(false);
	const [locationError, setLocationError] = useState<string | null>(null);
	const [dealerSearch, setDealerSearch] = useState<string>('');
	const [currentLocation, setCurrentLocation] = useState<{ latitude: number; longitude: number } | null>(null);
	useCMDIDropdownRBDMQuery();
	const { result: cmdiList } = useGetRxDb<CMDIDropdownRBDMItem>('mdi_code_list');
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [_, setRecommendedData] = useState<ClubRecommendationResponse>();

	const { mutate: clubRecommendationMutate } = useGetClubRecommendationsMutation();
	const { result: primaryDealersList } = useGetRxDb<IPrimaryDealerList>('dealers_list');
	const { result: dealerList } = useDealerListRxDb({
		searchParams: [
			{ key: 'dealername_c', value: dealerSearch },
			{ key: 'dealercode_c', value: dealerSearch },
		],
		sortBy: 'dealername_c',
	});

	const dealersList = useMemo(() => {
		if (dealerList?.length) {
			return [...dealerList]
				.sort((a, b) => a.dealername_c.localeCompare(b.dealername_c))
				.map((d) => ({
					id: d?.dealercode_c,
					value: `${d?.dealername_c} - ${d?.dealercode_c}`,
				}));
		}
		return [];
	}, [dealerList]);

	const checkTSIPermissions = useComponentLevelAccessCheck('bde-meeting-fields');

	useProfileQuery();
	const { result: profile } = useGetRxDb<ProfileData>('profile');
	const role = profile[0]?.usertype_c;
	const isRBDMUser = role === RoleType.RBDM;
	const isRCI = role === RoleType.RCI;
	const allowMultipleOfficeBearerClubs = canSelectMultipleOfficeBearerClubs(profile[0]?.mdicode_c);

	// State to track selected CMDI for enabling the query
	const [selectedCmdi, setSelectedCmdi] = useState<string | null>(null);

	// Call the query only when selectedCmdi is set (i.e., after dropdown selection)
	const { data: RBDMList, isFetching: isRBDMListFetching } = useRBDMClubDropdownQuery(selectedCmdi || '');
	// Use different initial values and validation for RBDM users
	const formik = useFormik({
		initialValues: isRBDMUser
			? (nonAttendedMembers as IRBDMAddMeetingFormInitialValues | null) ?? defaultRBDMAddMeetingInitialValues
			: (nonAttendedMembers as IDefaultAddMeetingFormInitialValues) || defaultAddMeetingInitialValues,
		onSubmit: () => {
			if (isRBDMUser) {
				handleRBDMSubmit();
			} else {
				handleSubmit();
			}
		},
		validationSchema: isRBDMUser ? RBDMAddMeetingValidationSchema : AddMeetingValidationSchema,
	});

	const navigate = useNavigate();
	const [clubRecommendation, setClubRecommendation] = useState<boolean>(false);
	const checkBdeIMR = useComponentLevelAccessCheck('imr-dropdown');

	const { result: clubOptions } = useGetRxDb<ClubDetails>('club');
	const { data: rbdmTownList } = useRbdmTownDetailsQuery(selectedCmdi || '');
	useMeetingsTypesQuery();
	const { result: meetingTypes } = useGetRxDb<MeetingType>('meeting_types');
	const meetingTypesFormatted = useMemo(
		() =>
			meetingTypes
				.filter((item) => {
					if (isRCI) {
						return item.name?.toUpperCase().includes('SGA');
					}
					return true;
				})
				.map((item) => ({
					id: item.id,
					value: item.name ?? '',
					sfid: item.sfid ?? '',
				})),
		[meetingTypes, isRCI]
	);

	useIMRCodeListQuery();
	const { result: IMRCodeList } = useGetRxDb<IMRCode>('imr_code_list');
	const IMRCodeListData = useMemo((): DropdownOptions[] => {
		if (IMRCodeList?.length) {
			return IMRCodeList?.map((imrCode) => ({
				id: imrCode.imr_code_c,
				value: imrCode.imr_code_c,
			}));
		}
		return [];
	}, [IMRCodeList]);

	const meetingMonth = data?.monthname_c ?? '';
	const financialYear = data?.financial_year_c ?? '';

	const [startYear, endYear] = financialYear ? financialYear.split('-') : [];

	const meetingYear = ['January', 'February', 'March'].includes(meetingMonth)
		? Number(`20${endYear}`)
		: Number(startYear);

	const currentDate = new Date();

	const monthIndex = new Date(`${meetingMonth} 1, ${meetingYear}`).getMonth();

	const isCurrentMonth = currentDate.getMonth() === monthIndex && currentDate.getFullYear() === meetingYear;

	const minDate = isCurrentMonth ? currentDate : new Date(meetingYear, monthIndex, 1);

	const maxDate = new Date(meetingYear, monthIndex + 1, 0);

	useEffect(() => {
		if (nonAttendedMembers === null && !isRBDMUser) {
			formik.setFieldValue('mdi', profile[0]?.mdicode_c);
			formik.setFieldValue('name', profile[0]?.name);
			formik.setFieldValue('contact', profile[0]?.phone_number_c);
			formik.setFieldValue('role', profile[0]?.usertype_c);
		}

		if (isRBDMUser && nonAttendedMembers === null) {
			formik.setFieldValue('name', profile[0]?.name);
			formik.setFieldValue('meetingType', 'Club Mela');
		}

		if (isMDPMeeting) {
			formik.setFieldValue('contact', profile[0]?.phone_number_c);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [nonAttendedMembers, profile, isRBDMUser, isMDPMeeting]);

	const isCityLeavelObMeetingType = (meetingType: string): boolean => {
		if (!meetingType) return false;
		const type = meetingType.toLowerCase();

		return CITY_LEAVEL_OB_MEET.includes(type);
	};

	const clubOptionsFormatted = useMemo(
		() => {
			const clubOptionsDropdown = isCityLeavelObMeetingType(formik?.values?.meetingType ?? '')
				? clubDropDownData
				: clubOptions;
			return (
				clubOptionsDropdown?.map((item) => ({
					id: item?.sfid ? item.sfid.toString() : item.id.toString(),
					value: item.name ?? '',
				})) ?? []
			);
		},
		[clubOptions, clubDropDownData, formik.values.meetingType] // Added missing dependencies
	);

	useEffect(() => {
		if (isCityLeavelObMeetingType(formik.values.meetingType)) {
			refetchClubDropDown?.();
		}
	}, [formik.values.meetingType, refetchClubDropDown]);

	useEffect(() => {
		if (isMDPMeeting && clubOptionsFormatted.length > 0) {
			const selectedClubNames = data?.cmditownlist_c?.split(',').map((item) => item.trim());
			if (selectedClubNames && formik.values.club.length === 0) {
				const selectedClubs = clubOptionsFormatted
					.filter((club) => selectedClubNames.includes(club.value))
					.map((club) => ({
						id: club.id,
						value: club.value,
					}));

				formik.setFieldValue('club', selectedClubs ?? []);
			}
		}
	}, [clubOptionsFormatted, isMDPMeeting, data?.cmditownlist_c]);

	useTownDetailsQuery();
	const { result: towns } = useGetRxDb<TownList>('towns');

	const townsList = useMemo(() => {
		if (towns?.length) {
			return towns?.map((t) => ({
				id: t.id,
				value: t.towncode_c + ' - ' + t.towndescription_c,
			}));
		}
		return [];
	}, [towns]);

	const electionEndDate = useMemo(() => {
		if (formik.values.electionNominationEndDate) {
			const startDate = new Date(formik.values.electionNominationEndDate);
			const endDate = new Date(startDate);
			endDate.setDate(endDate.getDate() + 10);
			return endDate.toISOString();
		}
		return '';
	}, [formik.values.electionNominationEndDate]);

	const electionNominationStartDateTime = useMemo(() => {
		if (formik.values.startdate && formik.values.start_time_c) {
			const date = new Date(formik.values.startdate);
			const day = date.getDate();
			const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
			const month = monthNames[date.getMonth()];
			const year = date.getFullYear().toString().slice(-2);
			return `${day}-${month}-${year} ${formik.values.start_time_c}`;
		}
		return '';
	}, [formik.values.startdate, formik.values.start_time_c]);

	const electionStartDateTime = useMemo(() => {
		const formValues = formik.values as IDefaultAddMeetingFormInitialValues;
		const endDate = formValues.electionNominationEndDate;
		const endTime = formValues.electionNominationEndTime;
		if (endDate && endTime) {
			const date = new Date(endDate);
			const day = date.getDate();
			const month = monthNames[date.getMonth()];
			const year = date.getFullYear().toString().slice(-2);
			return `${day}-${month}-${year} ${endTime}`;
		}
		return '';
	}, [
		(formik.values as IDefaultAddMeetingFormInitialValues).electionNominationEndDate,
		(formik.values as IDefaultAddMeetingFormInitialValues).electionNominationEndTime,
	]);

	const electionEndDateTime = useMemo(() => {
		const formValues = formik.values as IDefaultAddMeetingFormInitialValues;
		const endTime = formValues.electionNominationEndTime;
		if (electionEndDate && endTime) {
			const date = new Date(electionEndDate);
			const day = date.getDate();
			const month = monthNames[date.getMonth()];
			const year = date.getFullYear().toString().slice(-2);
			return `${day}-${month}-${year} ${endTime}`;
		}
		return '';
	}, [electionEndDate, (formik.values as IDefaultAddMeetingFormInitialValues).electionNominationEndTime]);

	useEffect(() => {
		if (data && nonAttendedMembers === null && !isRBDMUser) {
			const selectedTown = isMDPMeeting ? data?.town_c : data?.town_venue_c;
			const foundTown = towns.find((item) => item.towncode_c?.trim() === selectedTown?.trim());
			let contact = '';

			if (data?.mobileno_c) {
				contact = parseInt(data.mobileno_c, 10);
			} else if (isMDPMeeting) {
				contact = profile[0]?.phone_number_c ?? '';
			}
			const formValues = {
				name: data?.name,
				meetingType: data?.meeting_type_c,
				mdi: profile[0]?.mdicode_c,
				startdate: data?.startdate ? new Date(data.startdate).toISOString() : '',
				enddate: data?.enddate ? new Date(data.enddate).toISOString() : '',
				start_time_c: convertTime(data?.start_time_c),
				end_time_c: convertTime(data?.end_time_c),
				club:
					data?.club_name_c?.split(',').map((club, index) => ({
						value: club,
						id: data?.club_name_id_c?.split(',')[index] || '',
					})) ?? [],
				meetingVenue: data?.meeting_venue_c,
				meetingSfid: data?.meettype_c ?? '',
				meetFormat: 'Physical',
				product1Discussed: data?.focus_product_1,
				product2Discussed: data?.focus_product_2,
				primaryFocusProduct: data?.focus_product_4 ? data?.focus_product_4 : data?.focus_product_1_c ?? '',
				primaryFocusProductLcdMeets:
					data?.focus_product_4
						?.split(',')
						.map((productName) => {
							const cleanedName = productName.trim();

							const matchedIndex = availableProductsDataList?.findIndex(
								(product) => product.productgroup3description_c === cleanedName
							);

							if (matchedIndex === -1 || matchedIndex === undefined) return null;

							return {
								id: matchedIndex,
								value: availableProductsDataList[matchedIndex].productgroup3description_c,
							};
						})
						.filter(Boolean) ?? [],
				secondaryFocusProduct: data?.focus_product_5 ?? '',
				participants: !isMDPMeeting
					? data?.members?.map((item) => ({
							club_name_c: item.club_name_c,
							contactid: item.contactid,
							membershipno_c: item.membershipno_c,
						}))
					: [],
				contact: contact,
				town: foundTown?.towncode_c ? `${foundTown.towncode_c} - ${foundTown.towndescription_c}` : data?.town_venue_c,
				status: data?.status ?? '',
				meetingTheme: data?.meeting_theme_c ?? '',
				fccClub: data?.meeting_type_c === 'FCC PRODUCT AWARENESS MEET' ? data?.club_name_c : '',
				imr_code: [
					{ id: data.imr_code, value: data.imr_code },
					{ id: data.imr_code_2, value: data.imr_code_2 },
				].filter((item) => item.value != null),
				dealer: !isMDPMeeting ? `${data?.dealer_name_c} - ${data?.dealer_code_c}` : '',
				meetingLatitude: data?.meeting_latitude_c ?? '',
				meetingLongitude: data?.meeting_longitude_c ?? '',
				obMeetSelection: data?.ob_meet_selection ?? 'No',
				rbdm_code: data?.rbdm_code ?? '',
				meetSubCategory: isMDPMeeting ? data?.end_user_category_c : data?.meetsubcategory_c ?? '',
				electionNominationEndDate: data?.election_nomination_end_date_time
					? extractDateFromISO(data.election_nomination_end_date_time)
					: '',
				electionNominationEndTime: data?.election_nomination_end_date_time
					? convertTime(extractTime(data.election_nomination_end_date_time))
					: '',
			};

			formik.setValues(formValues, true);
			if (state?.id) {
				formik.validateForm(formValues).then(() => {
					Object.keys(formValues).forEach((fieldName) => {
						formik.setFieldTouched(fieldName, true, false);
					});
				});
			} else {
				formik.setFieldTouched('startdate', true);
				formik.setFieldTouched('endate', true);
				formik.setFieldTouched('start_time_c', true);
				formik.setFieldTouched('end_time_c', true);
			}
		}

		if (isMDPMeeting) {
			formik.setFieldValue('assigned_mdp_plan_c', data?.sfid);
			if (formik.values.participants === null || formik.values.participants === undefined) {
				formik.setFieldValue('participants', []);
			}
			const meetingSfid = meetingTypesFormatted.find((type) => type.value === formik.values.meetingType)?.sfid;
			formik.setFieldValue('meetingSfid', meetingSfid);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, nonAttendedMembers, towns, isRBDMUser]);

	const primaryDealers = useMemo(() => {
		if (primaryDealersList?.length) {
			return [...primaryDealersList]
				.sort((a, b) => a.dealername_c.localeCompare(b.dealername_c))
				.map((d) => ({
					id: d?.dealercode_c,
					value: `${d?.dealername_c} - ${d?.dealercode_c}`,
				}));
		}
		return [];
	}, [primaryDealersList]);

	const cmdiOptions = useMemo((): DropdownOptions[] => {
		if (cmdiList?.length) {
			return cmdiList?.map((cmdi) => ({
				id: cmdi.sfid,
				value: cmdi.mdicode_c,
			}));
		}
		return [];
	}, [cmdiList]);

	const rbdmClubOptions = useMemo((): RBDMClubOption[] => {
		if (RBDMList?.length) {
			const allClubs: RBDMClubDropdownItem[] = RBDMList as unknown as RBDMClubDropdownItem[];

			const options = allClubs.map((club) => ({
				id: club.sfid,
				value: club.name,
				sfid: club.sfid,
				name: club.name,
				assigned_cmdi: club.assigned_cmdi,
			}));

			return options;
		}
		return [];
	}, [RBDMList]);

	const handleGetCurrentLocation = useCallback((): void => {
		if (navigator.geolocation) {
			setLocationError(null);
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const { latitude, longitude } = position.coords;
					setCurrentLocation({ latitude, longitude });
					setIsLocationPermissionGranted(true);
					setLocationError(null);
				},
				(error) => {
					if (error.code === error.PERMISSION_DENIED) {
						setLocationError('Location access was denied. Please enable location access in your browser settings.');
					} else {
						setLocationError('Failed to get your location. Please try again.');
					}
					setIsLocationPermissionGranted(false);
				},
				{
					timeout: 10000,
					maximumAge: 60000,
					enableHighAccuracy: true,
				}
			);
		} else {
			setLocationError('Geolocation is not supported by this browser.');
			setIsLocationPermissionGranted(false);
		}
	}, []);

	const handleSelectedRBDMClubDropDown = useCallback(
		(selected: DropdownOptions): void => {
			if (!cmdiList) {
				setToaster(true, ToastType.error, 'CMDI list is not available');
				return;
			}
			const selectedCMDI = cmdiList.find((item) => item.mdicode_c === selected.value);
			if (selectedCMDI?.mdicode_c) {
				setSelectedCmdi(selectedCMDI.mdicode_c);
			} else {
				setSelectedCmdi(null);
				formik.setFieldValue('RBDMclub', '');
			}
		},
		[cmdiList, formik, setToaster]
	);

	const handleRBDMClubSelection = useCallback(
		(selected: DropdownOptions): void => {
			formik.setFieldValue('RBDMclub', selected.value);
		},
		[formik]
	);

	useEffect(() => {
		if (formik.values.cmdi) {
			const selectedCMDI = cmdiList.find((item) => item.mdicode_c === formik.values.cmdi);
			if (selectedCMDI?.mdicode_c) {
				setSelectedCmdi(selectedCMDI.mdicode_c);
			}
		}
	}, [formik.values.cmdi, cmdiList]);

	// RBDM Submit Handler
	const handleRBDMSubmit = useCallback(async () => {
		if (!isLocationPermissionGranted || !currentLocation) {
			handleGetCurrentLocation();

			setTimeout(() => {
				if (!isLocationPermissionGranted || !currentLocation) {
					setToaster(true, ToastType.error, 'Please allow location permission to continue.');
					return;
				}
			}, 1000);
			return;
		}
		const rbdmValues = formik.values as IRBDMAddMeetingFormInitialValues;

		const selectedCMDI = cmdiList.find((item) => item.mdicode_c === formik.values.cmdi);
		const selectedTown = rbdmTownList?.find((t) => t.towncode_c === rbdmValues.rbdmTown);

		const selectedRBDMClub = rbdmClubOptions.find((club) => club.value === formik.values.RBDMclub);
		if (!selectedRBDMClub) {
			setToaster(true, ToastType.error, 'Please select a valid club.');
			return;
		}
		const payload: RBDMMeetingDetails = {
			type: formik.values.meetingType,
			name: formik.values.name,
			startdate: formatDate(new Date(formik.values.startdate)),
			enddate: formatDate(new Date(formik.values.enddate)),
			start_time_c:
				generateTimeOptions()
					.find((item) => item.value === formik.values.start_time_c)
					?.id.toString() ?? '',
			end_time_c:
				generateTimeOptions()
					.find((item) => item.value === formik.values.end_time_c)
					?.id.toString() ?? '',
			meeting_theme_c: formik.values.meetingTheme,
			meeting_venue_c: formik.values.meetingVenue,
			latitude_against_town: currentLocation?.latitude,
			longitude_against_town: currentLocation?.longitude,
			assigned_cmdi_c: selectedCMDI?.sfid,
			cmdi_code_c: selectedCMDI?.mdicode_c,
			club_name_c: selectedRBDMClub?.name,
			club_name_id_c: selectedRBDMClub?.sfid,
			town_venue_c: selectedTown?.towncode_c ?? '',
			town_city_c: selectedTown?.towndescription_c ?? '',
		};

		rbdmMutate(payload, {
			onSuccess: () => {
				navigate('/rbdm/meetings');
				setToaster(true, ToastType.success, 'Meeting Added Successfully');
			},
			onError: (error) => {
				setToaster(true, ToastType.error, error.message);
			},
		});
	}, [
		formik.values,
		rbdmMutate,
		navigate,
		setToaster,
		isLocationPermissionGranted,
		currentLocation,
		cmdiList,
		RBDMList,
	]);

	useEffect(() => {
		if (isRBDMUser) {
			handleGetCurrentLocation();
		}
	}, [isRBDMUser, handleGetCurrentLocation]);

	// Original Submit Handler (unchanged for other users)
	const handleSubmit = useCallback(async () => {
		if (isRBDMUser) return; // Early return for RBDM users

		setIsCoordinatesFetching(true);
		const { latitude, longitude, error } = await getCoordinatesFromTownName(
			(formik.values as IDefaultAddMeetingFormInitialValues).town?.split('-')?.[1]?.trim()
		);
		if (error) {
			setIsCoordinatesFetching(false);
		}
		setIsCoordinatesFetching(false);
		if (
			compareDatePartsOnly(new Date(), new Date(formik.values.startdate)) &&
			isTimePast(formatTime(roundToNearestQuarterHour(new Date(formik.values.startdate))), formik.values.start_time_c)
		) {
			setToaster(true, ToastType.error, 'Unable to create past meetings');
		}
		if (
			BIG_MEETS.includes(formik.values.meetingType.toLowerCase()) &&
			!(formik.values as IDefaultAddMeetingFormInitialValues).meetingLatitude &&
			!(formik.values as IDefaultAddMeetingFormInitialValues).meetingLongitude &&
			!isMDPMeeting
		) {
			setToaster(true, ToastType.error, 'Please enter valid meeting venue');
			return;
		}
		const townInfo = getTownInfo(towns, (formik.values as IDefaultAddMeetingFormInitialValues).town);

		const payload: MeetingDetails = {
			type: isMDPMeeting || data?.type === 'MDP' ? 'MDP' : 'Non MDP',
			meeting_type_c: formik.values.meetingType,
			mdi_name_c: formik.values.name,
			startdate: formatDate(new Date(formik.values.startdate)),
			enddate: formatDate(new Date(formik.values.enddate)),
			start_time_c:
				generateTimeOptions()
					.find((item) => item.value === formik.values.start_time_c)
					?.id.toString() ?? '',
			end_time_c:
				generateTimeOptions()
					.find((item) => item.value === formik.values.end_time_c)
					?.id.toString() ?? '',
			club_name_c: (formik.values as IDefaultAddMeetingFormInitialValues).club.map((item) => item.value).join(),
			meeting_venue_c: formik.values.meetingVenue,
			meetFormat: 'Physical',
			meeting_theme_c: formik.values.meetingTheme,
			focus_product_1: (formik.values as IDefaultAddMeetingFormInitialValues).product1Discussed,
			focus_product_2: (formik.values as IDefaultAddMeetingFormInitialValues).product2Discussed,
			focus_product_4:
				formik?.values?.meetingType === LCD_MEETS_SHOP_HALL
					? (formik?.values?.primaryFocusProductLcdMeets?.map((item) => item.value).join() as string)
					: (formik.values as IDefaultAddMeetingFormInitialValues).primaryFocusProduct,
			focus_product_5: (formik.values as IDefaultAddMeetingFormInitialValues).secondaryFocusProduct,
			status: (formik.values as IDefaultAddMeetingFormInitialValues).status,
			members: (formik.values as IDefaultAddMeetingFormInitialValues).participants,
			club_name_id_c: (formik.values as IDefaultAddMeetingFormInitialValues).club
				.map((item) => item.id.toString())
				.join(),
			town_venue_c: townInfo?.towncode ?? '',
			longitude_against_town: longitude ?? 0,
			latitude_against_town: latitude ?? 0,
			mobileno_c: (formik.values as IDefaultAddMeetingFormInitialValues).contact.toString(),
			town_city: townInfo?.townname ?? '',
			meeting_latitude_c: (formik.values as IDefaultAddMeetingFormInitialValues)?.meetingLatitude,
			meeting_longitude_c: (formik.values as IDefaultAddMeetingFormInitialValues)?.meetingLongitude,
			ob_meet_selection: (formik.values as IDefaultAddMeetingFormInitialValues)?.obMeetSelection,
			meettype_c: formik.values.meetingSfid,
		};

		if (isMDPMeeting) {
			payload.assigned_mdp_plan_c = formik.values.assigned_mdp_plan_c ?? null;
		}

		if (!checkBdeIMR) {
			const [imrCode1, imrCode2] = (formik.values as IDefaultAddMeetingFormInitialValues).imr_code;
			imrCode1?.value && (payload.imr_code = imrCode1.value.toString());
			imrCode2?.value && (payload.imr_code_2 = imrCode2.value.toString());
		}

		if (checkTSIPermissions) {
			const dealer = primaryDealers.find(
				(item) => item.value === (formik.values as IDefaultAddMeetingFormInitialValues).dealer
			);
			if (dealer) {
				payload.dealer_name_c = dealer?.value.split('-')[0]?.trim();
				payload.dealer_code_c = dealer?.id;
			}
		}
		payload.meetsubcategory_c = formik?.values?.meetingType === SocialMeetType ? formik?.values?.meetSubCategory : '';

		if (formik?.values?.meetingType === IsLcdWiseUserMeet) {
			payload.dealer_name_c = formik?.values?.dealer?.split('-')[0]?.trim();
			payload.dealer_code_c = formik?.values?.dealer?.split('-')[1]?.trim();
		}

		// Add this helper function at the top of your component or in a utils file
		const isDateObject = (value: unknown): value is Date => {
			return value instanceof Date && !isNaN(value.getTime());
		};

		// Handle election nomination end date time based on OB election selection
		const isEditMode = !!state?.id;
		const formValues = formik.values as IDefaultAddMeetingFormInitialValues;

		if (formValues?.obMeetSelection === 'Yes') {
			// On add or edit: If OB election is checked, include election_nomination_end_date_time
			if (formValues.electionNominationEndDate && formValues.electionNominationEndTime) {
				// Convert time
				const [time, period] = formValues.electionNominationEndTime.split(' ');
				const [hoursStr, minutesStr] = time.split(':');
				let hours = parseInt(hoursStr, 10);
				const minutes = parseInt(minutesStr, 10);

				if (period?.toLowerCase() === 'pm' && hours !== 12) hours += 12;
				if (period?.toLowerCase() === 'am' && hours === 12) hours = 0;

				// SAFE & TYPE-SAFE DATE STRING EXTRACTION
				let dateString = '';

				const rawDate = formValues.electionNominationEndDate;

				if (isDateObject(rawDate)) {
					// It's a real Date object
					dateString = `${rawDate.getFullYear()}-${String(rawDate.getMonth() + 1).padStart(2, '0')}-${String(rawDate.getDate()).padStart(2, '0')}`;
				} else if (typeof rawDate === 'string') {
					// It's a string (ISO or YYYY-MM-DD)
					dateString = rawDate.split('T')[0]; // "2025-11-30T00:00:00.000Z" → "2025-11-30"
				}

				const [year, month, day] = dateString.split('-');

				const hoursFormatted = String(hours).padStart(2, '0');
				const minutesFormatted = String(minutes).padStart(2, '0');

				payload.election_nomination_end_date_time = `${year}-${month}-${day}T${hoursFormatted}:${minutesFormatted}:00Z`;
			}
		} else if (isEditMode && formValues?.obMeetSelection === 'No') {
			// On edit: If OB election is unchecked, set election_nomination_end_date_time to empty string
			payload.election_nomination_end_date_time = null;
		}

		mutate(payload, {
			onSuccess: () => {
				if (state?.id && !isMDPMeeting) {
					navigate('/meeting-details', { state: { id: state?.id } });
					setToaster(true, ToastType.success, 'Meeting Updated Successfully');
					resetMembersStore();
				} else {
					navigate('/meetings');
					setToaster(true, ToastType.success, 'Meeting Added Successfully');
					refetch?.();
					resetMembersStore();
				}
			},
			onError: (error) => {
				setToaster(true, ToastType.error, error.message);
			},
		});
	}, [
		formik.values,
		towns,
		checkBdeIMR,
		checkTSIPermissions,
		mutate,
		setToaster,
		primaryDealers,
		state?.id,
		navigate,
		resetMembersStore,
		refetch,
		isRBDMUser,
		isMDPMeeting,
	]);

	const handleSubmitClick = (): void => {
		formik.handleSubmit();
	};

	useEffect(() => {
		if (
			compareDatePartsOnly(new Date(), new Date(formik.values.startdate)) &&
			isTimePast(formatTime(roundToNearestQuarterHour(new Date(formik.values.startdate))), formik.values.start_time_c)
		) {
			formik.setFieldValue('start_time_c', formatTime(roundToNearestQuarterHour(new Date(formik.values.startdate))));
		}
		if (formik.values.start_time_c && formik.values.startdate) {
			const startDate = new Date(formik.values.startdate);
			const { endTime, endDate } = calculateEndTimeAndDate(formik.values.start_time_c, startDate);
			formik.setFieldValue('end_time_c', endTime);
			if (
				endDate.getDate() !== startDate.getDate() ||
				endDate.getMonth() !== startDate.getMonth() ||
				endDate.getFullYear() !== startDate.getFullYear()
			) {
				formik.setFieldValue('enddate', endDate);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formik.values.start_time_c, formik.values.startdate]);

	// Auto-fill Election Nomination End Date from meeting end date
	// Only auto-fill for new meetings, not when editing (to preserve API data)
	useEffect(() => {
		const formValues = formik.values as IDefaultAddMeetingFormInitialValues;
		// Only auto-fill if:
		// 1. OB election is enabled
		// 2. End date exists
		// 3. We're NOT in edit mode (state?.id doesn't exist)
		// 4. The field is empty (to preserve manually entered values when returning from manage participants)
		const isEditMode = !!state?.id;
		const shouldAutoFill =
			formValues.obMeetSelection === 'Yes' &&
			formValues.enddate &&
			!isEditMode &&
			!formValues.electionNominationEndDate;

		if (shouldAutoFill) {
			formik.setFieldValue('electionNominationEndDate', formValues.enddate);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		(formik.values as IDefaultAddMeetingFormInitialValues).enddate,
		(formik.values as IDefaultAddMeetingFormInitialValues).obMeetSelection,
		(formik.values as IDefaultAddMeetingFormInitialValues).electionNominationEndDate,
		state?.id,
	]);

	const handleClubRecommended = useCallback(
		(id: string) => {
			clubRecommendationMutate(
				{
					club_id: id,
					start_date: formatDate(new Date(formik?.values?.startdate)),
				},
				{
					onSuccess: (data) => {
						setRecommendedData(data);
						setClubRecommendation(true);
					},
					onError(error) {
						setToaster(true, ToastType.error, error.message);
					},
				}
			);
		},
		[clubRecommendationMutate, formik?.values?.startdate, setToaster]
	);

	const handleClubValidationModal = useCallback(() => {
		setClubRecommendation(false);
		setRecommendedData(undefined);
	}, []);

	const renderClubSelection = (): JSX.Element | null => {
		if (checkTSIPermissions || isRBDMUser) return null;
		const meetingType = formik.values.meetingType ?? '';
		const isFCCMeet = meetingType === 'FCC PRODUCT AWARENESS MEET';
		const isOfficeBearerMeet = isOfficeBearerMeetType(meetingType);
		const maxLimit = isOfficeBearerMeet && !allowMultipleOfficeBearerClubs ? 1 : MaxLimitMap[meetingType];

		return (
			<li className="d-col d-col-2">
				<MultiselectDropdown
					id="club"
					name="club"
					label="Club"
					onBlur={formik.handleBlur}
					values={(formik.values as IDefaultAddMeetingFormInitialValues).club}
					options={clubOptionsFormatted}
					maxLimit={maxLimit}
					setFieldValue={formik.setFieldValue}
					required={!isRCI}
					className="input-border"
					onChange={(e) => {
						const user = clubOptionsFormatted.find((item) => item.id === e.id);
						if (isFCCMeet) {
							if ((formik.values as IDefaultAddMeetingFormInitialValues).club.length === 0) {
								handleClubRecommended(user?.id.toString() ?? '');
							}
						}
					}}
					disabled={isMDPMeeting}
				/>
			</li>
		);
	};

	const isManageParticipantsDisabled = useMemo(() => {
		if (isRBDMUser) return true; // Always disabled for RBDM users

		const dealerCode = (formik.values as IDefaultAddMeetingFormInitialValues).dealer?.split('-')?.[1]?.trim();
		const foundDealer = primaryDealersList?.some((d) => d?.dealercode_c === dealerCode);
		const userType = profile[0]?.usertype_c;

		return (
			(userType !== RoleType.TSI && !(formik.values as IDefaultAddMeetingFormInitialValues).club?.length) ||
			(userType === RoleType.TSI && !foundDealer)
		);
	}, [
		primaryDealersList,
		profile,
		formik.values.meetingType,
		(formik.values as IDefaultAddMeetingFormInitialValues).club?.length,
		(formik.values as IDefaultAddMeetingFormInitialValues).dealer,
		isRBDMUser,
	]);

	const handleVenueSelection = (selectedData: IMapSearchInput): void => {
		const { lat, lng, name, formattedAddress } = selectedData;
		formik.setFieldValue('meetingLatitude', lat);
		formik.setFieldValue('meetingLongitude', lng);

		formik.setFieldValue('meetingVenue', name + ' - ' + formattedAddress);
	};

	const onDropDownSearch = useDebounce((e: React.KeyboardEvent<HTMLInputElement>): void => {
		const element = e.target as HTMLInputElement;
		setDealerSearch(element.value);
	}, 300);

	const availableProductsList = useMemo((): AvailableProducts[] => {
		if (availableProductsDataList?.length) {
			return availableProductsDataList.map((product, index) => ({
				id: index,
				value: product?.productgroup3description_c,
			}));
		}
		return [];
	}, [availableProductsDataList]);

	const getMinimumMeetingDate = (): Date => {
		const today = new Date();

		if (formik.values.meetingType !== START_DATE_RESTRICTION_MEET) {
			return today;
		}
		const minimumDate = new Date(today);
		minimumDate.setDate(today.getDate() + 5);

		return minimumDate;
	};

	const getMinimumMeetingEndDate = (): Date => {
		if (formik.values.startdate) {
			return new Date(formik.values.startdate);
		}

		return getMinimumMeetingDate();
	};

	return (
		<>
			<div className="form-popup show">
				<section className="new-meeting-section new-site-desktop overflow desktop-popup">
					<div className="container">
						<Wrapper isError={error} isLoaderVisible={isFetching}>
							<>
								<form onSubmit={formik.handleSubmit}>
									<ul className="new-meeting-wrapper row">
										<li className="form-control d-col d-col-2 ">
											<Dropdown
												id="meetingType"
												name="meetingType"
												label="Meeting Type"
												onBlur={formik.handleBlur}
												title={formik.values.meetingType ?? ''}
												error={
													formik.touched.meetingType && formik.errors.meetingType ? formik.errors.meetingType : null
												}
												options={
													isRBDMUser
														? [{ id: 'Club Mela', value: 'Club Mela' }]
														: checkTSIPermissions
															? TSIMeetingTypes
															: meetingTypesFormatted
												}
												setFieldValue={formik.setFieldValue}
												required
												disabled={
													isMDPMeeting ||
													isRBDMUser ||
													(state?.id && (formik.values as IDefaultAddMeetingFormInitialValues).rbdm_code)
												}
												onChange={(e) => {
													const meetType = meetingTypes.find((item) => item.name === e.value);
													const sfid = meetType?._data?.sfid ?? '';
													formik.setFieldValue('meetingSfid', sfid);
													if (isCityLeavelObMeetingType(e.value)) {
														refetchClubDropDown?.();
													}
													formik.setFieldValue('meetingVenue', '');
													formik.setFieldValue('meetSubCategory', '');
													formik.setFieldValue('club', []);
													formik.setFieldValue('dealer', '');
													formik.setFieldValue('meetingTheme', '');
													formik.setFieldValue('primaryFocusProductLcdMeets', []);
													formik.setFieldValue('primaryFocusProduct', '');
													formik.setFieldValue('secondaryFocusProduct', '');
													formik.setFieldValue('product1Discussed', '');
													formik.setFieldValue('product2Discussed', '');
													formik.setFieldValue('startdate', new Date().toISOString());
													formik.setFieldValue(
														'enddate',
														new Date(roundToNearestQuarterHour(new Date()).getTime() + 60 * 60 * 1000).toISOString()
													);

													if (e.value === 'FCC PRODUCT AWARENESS MEET') {
														formik.setFieldValue('club', []);
														formik.setFieldValue('fccClub', '');
														formik.setFieldValue('participants', []);
														formik.setFieldValue('startdate', fccProductAwarnessStartDate.toISOString());
														const endDate = new Date(fccProductAwarnessStartDate.getTime() + 60 * 60 * 1000);
														formik.setFieldValue('enddate', endDate.toISOString());
													}
													if (!BIG_MEETS.includes(e.value)) {
														formik.setFieldValue('meetingLongitude', '');
														formik.setFieldValue('meetingLatitude', '');
													}
												}}
											/>
										</li>

										{/* Name Field - Always visible */}
										<li className="form-control d-col d-col-2 ">
											<TextInput
												id="name"
												name="name"
												type="text"
												onBlur={formik.handleBlur}
												value={formik.values.name ?? ''}
												setFieldValue={formik.setFieldValue}
												label="Name"
												error={formik.touched.name && formik.errors.name ? formik.errors.name : null}
												required
												disabled
											/>
										</li>

										{/* MDI Field - Only for non-RBDM users */}
										{!isRBDMUser && (
											<li className="form-control d-col d-col-2 location">
												<TextInput
													id="mdi"
													name="mdi"
													onBlur={formik.handleBlur}
													type="text"
													value={formik.values.mdi ?? ''}
													setFieldValue={formik.setFieldValue}
													label="MDI"
													error={formik.touched.mdi && formik.errors.mdi ? formik.errors.mdi : null}
													required
													disabled
												/>
											</li>
										)}

										{/* Contact Field - Only for non-RBDM users */}
										{!isRBDMUser && (
											<li className="form-control d-col d-col-2 ">
												<TextInput
													id="contact"
													name="contact"
													type="text"
													onBlur={formik.handleBlur}
													value={formik.values.contact ?? ''}
													setFieldValue={formik.setFieldValue}
													label="Contact"
													error={formik.touched.contact && formik.errors.contact ? formik.errors.contact : null}
													onKeyDown={keyDownEvent}
													maxLength={10}
													required
													disabled
												/>
											</li>
										)}

										{/* Date and Time Fields - Always visible */}
										<li className="d-col">
											<div className="meeting-box-wrapper">
												<ul>
													<li>
														<SingleDatePicker
															className="single-date-picker"
															label="Meeting Start Date"
															name="startdate"
															id="startdate"
															value={formik.values.startdate}
															error={
																formik.touched.startdate && formik.errors.startdate ? formik.errors.startdate : null
															}
															required={true}
															formik={formik}
															minDate={isMDPMeeting ? minDate : state?.id && data ? undefined : getMinimumMeetingDate()}
															maxDate={isMDPMeeting ? maxDate : undefined}
															onChange={(value) => {
																formik.setFieldValue('enddate', value);
																const isEditMode = !!state?.id;
																const formValues = formik.values as IDefaultAddMeetingFormInitialValues;
																if (
																	isEditMode &&
																	formValues.obMeetSelection === 'Yes' &&
																	(formValues.electionNominationEndDate || formValues.electionNominationEndTime)
																) {
																	formik.setFieldValue('electionNominationEndDate', '');
																	formik.setFieldValue('electionNominationEndTime', '');
																}
															}}
														/>
													</li>
													<li>
														<SingleDatePicker
															className="single-date-picker"
															id="enddate"
															name="enddate"
															value={formik.values.enddate}
															label="Meeting End Date"
															formik={formik}
															required={true}
															error={formik.touched.enddate && formik.errors.enddate ? formik.errors.enddate : null}
															minDate={state?.id && data?.startdate ? undefined : getMinimumMeetingEndDate()}
														/>
													</li>
													<li>
														<TimePicker
															name="start_time_c"
															id="start_time_c"
															label="Meeting Start Time"
															error={
																formik.touched.start_time_c && formik.errors.start_time_c
																	? formik.errors.start_time_c
																	: null
															}
															required={true}
															value={formik.values.start_time_c}
															formik={formik}
															icon={'icon-clock'}
															minTime={
																compareDatePartsOnly(new Date(), new Date(formik.values.startdate))
																	? formatTime(roundToNearestQuarterHour(new Date(formik.values.startdate)))
																	: ''
															}
														/>
													</li>

													<li>
														<TimePicker
															name="end_time_c"
															id="end_time_c"
															label="Meeting End Time"
															error={
																formik.touched.end_time_c && formik.errors.end_time_c ? formik.errors.end_time_c : null
															}
															required={true}
															value={formik.values.end_time_c}
															formik={formik}
															icon={'icon-clock'}
														/>
													</li>
												</ul>
											</div>
										</li>

										{isRBDMUser && (
											<li className="d-col d-col-2">
												<Dropdown
													id="cmdi"
													name="cmdi"
													label="CMDI"
													onBlur={formik.handleBlur}
													title={formik.values.cmdi ?? ''}
													error={formik.touched.cmdi && formik.errors.cmdi ? formik.errors.cmdi : null}
													options={cmdiOptions}
													setFieldValue={formik.setFieldValue}
													required
													onChange={handleSelectedRBDMClubDropDown}
												/>
											</li>
										)}

										{/* RBDM Club Dropdown - Only for RBDM users and when CMDI is selected */}

										{isRBDMUser && (
											<li className="d-col d-col-2">
												<Dropdown
													id="RBDMclub"
													name="RBDMclub"
													label="Club"
													onBlur={formik.handleBlur}
													title={isRBDMListFetching ? 'Loading clubs...' : formik.values.RBDMclub || ''}
													error={formik.touched.RBDMclub && formik.errors.RBDMclub ? formik.errors.RBDMclub : null}
													options={rbdmClubOptions}
													setFieldValue={formik.setFieldValue}
													required
													disabled={isRBDMListFetching}
													onChange={handleRBDMClubSelection}
												/>
											</li>
										)}

										{isRBDMUser && (
											<li className="d-col d-col-2">
												<Dropdown
													id="rbdmTown"
													name="rbdmTown"
													label="Town"
													onBlur={formik.handleBlur}
													title={
														rbdmTownList?.find(
															(t) => t.towncode_c === (formik.values as IRBDMAddMeetingFormInitialValues).rbdmTown
														)
															? `${(formik.values as IRBDMAddMeetingFormInitialValues).rbdmTown} - ${
																	rbdmTownList?.find(
																		(t) => t.towncode_c === (formik.values as IRBDMAddMeetingFormInitialValues).rbdmTown
																	)?.towndescription_c
																}`
															: ''
													}
													error={formik.touched.rbdmTown && formik.errors.rbdmTown ? formik.errors.rbdmTown : null}
													options={
														rbdmTownList
															?.filter((t) => t.towncode_c && t.towndescription_c)
															.map((t) => ({
																id: t.towncode_c as string,
																value: `${t.towncode_c} - ${t.towndescription_c}`,
															})) ?? []
													}
													setFieldValue={(field: string, value: string, shouldValidate?: boolean) => {
														const selectedOption = rbdmTownList?.find(
															(t) => `${t.towncode_c} - ${t.towndescription_c}` === value
														);
														formik.setFieldValue(field, selectedOption?.towncode_c ?? '', shouldValidate);
													}}
													required
													disabled={!selectedCmdi}
													placeholder={selectedCmdi ? 'Select Town' : 'Select CMDI first'}
												/>
											</li>
										)}

										{/* OB Election - Only for BDE */}
										{role === RoleType.BDE && !isRBDMUser && (
											<>
												<li className="d-col d-col-1">
													<Checkbox
														id="obMeetSelection"
														name="obMeetSelection"
														type="checkbox"
														checked={formik?.values?.obMeetSelection === 'Yes'}
														onChange={(e) => {
															const isChecked = typeof e === 'boolean' ? e : e.target?.checked;
															formik.setFieldValue('obMeetSelection', isChecked ? 'Yes' : 'No');
														}}
														label="Enable OB election"
													/>
												</li>

												{formik?.values?.obMeetSelection === 'Yes' && (
													<>
														<li className="form-control d-col d-col-2">
															<TextInput
																id="electionNominationStartDate"
																name="electionNominationStartDate"
																type="text"
																onBlur={formik.handleBlur}
																value={electionNominationStartDateTime}
																setFieldValue={formik.setFieldValue}
																label="Election Nomination Start Date and time"
																disabled={true}
															/>
														</li>

														<li className="d-col d-col-4">
															<SingleDatePicker
																className="single-date-picker"
																id="electionNominationEndDate"
																name="electionNominationEndDate"
																label="Election Nomination End Date"
																value={formik.values.electionNominationEndDate}
																formik={formik}
																error={
																	formik.touched.electionNominationEndDate && formik.errors.electionNominationEndDate
																		? formik.errors.electionNominationEndDate
																		: null
																}
																required={true}
																minDate={formik.values.startdate ? new Date(formik.values.startdate) : new Date()}
															/>
														</li>

														<li className="d-col d-col-4">
															<TimePicker
																name="electionNominationEndTime"
																id="electionNominationEndTime"
																label="Election Nomination End Time"
																value={formik.values.electionNominationEndTime}
																formik={formik}
																error={
																	formik.touched.electionNominationEndTime && formik.errors.electionNominationEndTime
																		? formik.errors.electionNominationEndTime
																		: null
																}
																required={true}
																icon={'icon-clock'}
															/>
														</li>

														<li className="form-control d-col d-col-2">
															<TextInput
																id="electionStartDate"
																name="electionStartDate"
																type="text"
																onBlur={formik.handleBlur}
																value={electionStartDateTime}
																setFieldValue={formik.setFieldValue}
																label="Election Start Date and time"
																disabled={true}
															/>
														</li>

														<li className="form-control d-col d-col-2">
															<TextInput
																id="electionEndDate"
																name="electionEndDate"
																type="text"
																onBlur={formik.handleBlur}
																value={electionEndDateTime}
																setFieldValue={formik.setFieldValue}
																label="Election End Date and time"
																disabled={true}
															/>
														</li>
													</>
												)}
											</>
										)}

										{formik.values.meetingType === SocialMeetType && (
											<li className="d-col d-col-2  ">
												<Dropdown
													id="meetSubCategory"
													name="meetSubCategory"
													label="Social Meet Sub-Type"
													onBlur={formik.handleBlur}
													title={formik.values.meetSubCategory ?? ''}
													error={
														formik.touched.meetSubCategory && formik.errors.meetSubCategory
															? formik.errors.meetSubCategory
															: null
													}
													options={SocialMeetSubTypes}
													setFieldValue={formik.setFieldValue}
													required
													disabled={isMDPMeeting}
												/>
											</li>
										)}

										{/* Town Field - Only for non-RBDM users */}
										{!isRBDMUser && (
											<li className="d-col d-col-2  ">
												<Dropdown
													id="town"
													name="town"
													label="Town"
													onBlur={formik.handleBlur}
													title={formik.values.town ?? ''}
													error={formik.touched.town && formik.errors.town ? formik.errors.town : null}
													options={townsList}
													setFieldValue={formik.setFieldValue}
													required
													disabled={isMDPMeeting}
												/>
											</li>
										)}

										{/* Club Selection - Only for non-RBDM, non-TSI users */}
										{!MEETING_HIDE_CLUB.includes((formik.values.meetingType ?? '').toLowerCase()) &&
											renderClubSelection()}

										{/* Dealer Selection - Only for TSI */}
										{((checkTSIPermissions && !isRBDMUser) || formik?.values?.meetingType === IsLcdWiseUserMeet) && (
											<li className="d-col d-col-2  ">
												<Dropdown
													id="dealer"
													name="dealer"
													label={formik?.values?.meetingType === LCD_MEETS_SHOP_HALL ? 'Group Code' : 'Dealer'}
													onBlur={formik.handleBlur}
													title={formik.values.dealer ?? ''}
													error={formik.touched.dealer && formik.errors.dealer ? formik.errors.dealer : null}
													options={formik?.values?.meetingType === IsLcdWiseUserMeet ? dealersList : primaryDealers}
													setFieldValue={formik.setFieldValue}
													handelKeyDownProp={onDropDownSearch}
													listFetching={true}
													required
												/>
											</li>
										)}

										{/* Meeting Venue - Always visible */}
										<li className="form-control d-col d-col-2">
											{!isRBDMUser && BIG_MEETS.includes(formik.values.meetingType?.toLowerCase() ?? '') ? (
												<GoogleSearchInput
													id="meetingVenue"
													name="meetingVenue"
													label="Search Meeting Venue"
													value={formik.values.meetingVenue}
													onBlur={formik.handleBlur}
													setFieldValue={formik.setFieldValue}
													error={
														formik.touched.meetingVenue && formik.errors.meetingVenue
															? formik.errors.meetingVenue
															: null
													}
													required
													handleVenueSelection={handleVenueSelection}
												/>
											) : (
												<TextInput
													id="meetingVenue"
													name="meetingVenue"
													type="text"
													onBlur={formik.handleBlur}
													value={formik.values.meetingVenue ?? ''}
													setFieldValue={formik.setFieldValue}
													label="Meeting Venue"
													error={
														formik.touched.meetingVenue && formik.errors.meetingVenue
															? formik.errors.meetingVenue
															: null
													}
													required
													labelClass="meeting-venue-label"
												/>
											)}
										</li>

										{/* Meeting Theme - Always visible */}
										{!MEETING_THEME_HIDE_MEETING_TYPE.includes(formik.values.meetingType) && (
											<li className="d-col d-col-2  ">
												<Dropdown
													id="meetingTheme"
													name="meetingTheme"
													label="Meeting Theme"
													onBlur={formik.handleBlur}
													title={formik.values.meetingTheme ?? ''}
													error={
														formik.touched.meetingTheme && formik.errors.meetingTheme
															? formik.errors.meetingTheme
															: null
													}
													options={meetingTheme}
													setFieldValue={formik.setFieldValue}
													required={isRBDMUser}
												/>
											</li>
										)}

										{/* Products Fields - Only for non-RBDM users */}
										{!isRBDMUser && !MEETING_HIDE_FOCUS_PRODUCT.includes(formik.values.meetingType) && (
											<AddProductsFields
												formik={formik}
												primaryFocusProduct={'primaryFocusProduct'}
												secondaryFocusProduct={'secondaryFocusProduct'}
												product1Discussed={'product1Discussed'}
												product2Discussed={'product2Discussed'}
												isEditing={false}
												meetingType={
													isMDPMeeting && PRODUCT_SPECIFIC_MEETS.includes(formik.values.meetingType.toLowerCase())
														? 'MDP'
														: 'Non MDP'
												}
											/>
										)}
										{formik?.values?.meetingType === LCD_MEETS_SHOP_HALL && !isRBDMUser && (
											<li className="d-col d-col-2  ">
												<MultiselectDropdown
													id="primaryFocusProductLcdMeets"
													name="primaryFocusProductLcdMeets"
													label="Primary Focus Products (Max 3)"
													onBlur={formik.handleBlur}
													error={
														formik.touched.primaryFocusProductLcdMeets && formik.errors.primaryFocusProductLcdMeets
															? formik.errors.primaryFocusProductLcdMeets
															: null
													}
													className="input-border"
													options={availableProductsList}
													values={formik.values.primaryFocusProductLcdMeets || []}
													setFieldValue={formik.setFieldValue}
													maxLimit={3}
													required
												/>
											</li>
										)}

										{/* Meet Format - Only for non-RBDM users */}
										{!isRBDMUser && (
											<li className="d-col d-col-2  ">
												<Dropdown
													id="meetFormat"
													name="meetFormat"
													label="Meet Format"
													onBlur={formik.handleBlur}
													title={formik.values.meetFormat ?? ''}
													error={
														formik.touched.meetFormat && formik.errors.meetFormat ? formik.errors.meetFormat : null
													}
													options={meetFormate}
													setFieldValue={formik.setFieldValue}
													required
													disabled
												/>
											</li>
										)}

										{/* IMR Code - Only for non-RBDM, non-BDE IMR users */}
										{!checkBdeIMR && !isRBDMUser && (
											<li className="d-col d-col-2  ">
												<MultiselectDropdown
													id="imr_code"
													name="imr_code"
													label="Imr Code"
													onBlur={formik.handleBlur}
													options={IMRCodeListData}
													setFieldValue={formik.setFieldValue}
													values={formik.values.imr_code}
													className="input-border"
													maxLimit={2}
												/>
											</li>
										)}

										{/* Manage Participants - Only for non-RBDM */}
										{!isRBDMUser && !MEETING_PARTICIPANTS_REMOVE.includes(formik.values.meetingType) && (
											<li className="d-col d-col-2">
												<div
													className={`manage-participants-button ${isManageParticipantsDisabled && !isRCI && !MEETING_HIDE_CLUB.includes(formik.values.meetingType?.toLowerCase() ?? '') ? 'disable-btn' : ''}`}
												>
													<div className="label">
														<span className="required content">Manage Participants</span>
														{(formik.values as IDefaultAddMeetingFormInitialValues).participants?.length !== 0 && (
															<span className="count">
																{(formik.values as IDefaultAddMeetingFormInitialValues).participants?.length}
															</span>
														)}
													</div>
													<NavLink
														to={
															state?.id ? `/edit-meeting/manage-participants` : '/add-new-meeting/manage-participants'
														}
														aria-label={state?.id ? 'edit participants' : 'add participants'}
														className="add-user"
														onClick={() => {
															setNonAttendedMembers(formik.values as IDefaultAddMeetingFormInitialValues);
														}}
													>
														<span className="icon-add-user"></span>
														<span className="icon-right-arrow"></span>
													</NavLink>
												</div>
											</li>
										)}
									</ul>
								</form>
								<SubmitButton
									label="Submit"
									isDisabled={
										isRBDMUser
											? !formik.isValid
											: !formik.isValid ||
												(formik.values.participants?.length == 0 &&
													!MEETING_PARTICIPANTS_REMOVE.includes(formik.values.meetingType) &&
													!isRCI) ||
												isCoordinatesFetching
									}
									isEditing={false}
									handleSubmitClick={handleSubmitClick}
									isPending={isRBDMUser ? isRBDMMeetingFormPending : isMeetingFormPending}
								/>
							</>
						</Wrapper>
					</div>
				</section>
			</div>
			{!isRBDMUser && (
				<Modal
					modalId="metingModal"
					className="dialog-sm pt-25"
					modalOpen={clubRecommendation}
					onModalClose={handleClubValidationModal}
				>
					<ClubRecommendation />
				</Modal>
			)}
		</>
	);
};
